/**
 * BattleCode Demo v1.0.1 — Engine Scheduler (main thread)
 *
 * 协调 2 个 Pyodide Worker（player + AI），跑 10Hz 逻辑 tick + 30Hz 渲染。
 *
 * 关键约束（demo-gdd §11.6 LOCK）：
 *   Q1=B Web Worker（真隔离）
 *   Q3=A V14: 10Hz 逻辑 tick（100ms）+ 独立 render loop（~60fps rAF）
 *   Q4=B Promise.all 并行（双 bot 同时刻同 state，独立 think）
 *   Q5=A 50ms 软超时（Worker 内 walltime 监测 + 上报 timed_out=true）
 *   Q5=B 200ms 硬超时（主线程 setTimeout watchdog + worker.terminate + 重建）
 *   Q6 Pre-flight（5 tick mini scenario，<250ms 总耗时）+ Runtime 兜底（3 strikes = 中止）
 *
 * 公共 API：
 *   - start(playerCode, aiCode) → Promise<void>
 *   - preflight(playerCode) → Promise<PreFlightResult>  // 屏 2 调用
 *   - on(listener) / off(listener)
 *   - stop()
 */

import AI_BOT_CODE from "./ai-bot.py?raw";
import { PRE_FLIGHT_TICKS, makePreFlightTicks, type PreFlightTickDef } from "./pre-flight-scenarios";
import {
  BattleState,
  BotAction,
  BotTickResult,
  DEFAULT_ENGINE_CONFIG,
  EngineConfig,
  EngineEvent,
  EngineEventListener,
  PreFlightResult,
  WorkerInbound,
  WorkerOutbound,
} from "./types";
import { ERROR_CODES } from "./errorCodes";
// 2026-07-06:屏 2 preflight log panel 消费
//   - engine 转发 worker stdout/stderr 到 onLog callback
//   - engine 自身状态(phase 切换)走 onPhase,source="engine"
export interface PreflightLogEntry {
  source: "player" | "ai" | "engine";
  level: "info" | "warn" | "error";
  message: string;
  ts: number;
}

export interface PreflightOptions {
  /** 每次 stdout/stderr 流回时触发(屏 2 实时追加 log 行) */
  onLog?: (entry: PreflightLogEntry) => void;
  /** preflight 阶段切换触发(屏 2 显示"加载 Pyodide 运行时..."等状态行) */
  onPhase?: (phase: string) => void;
}
// P1-#4:Pyodide Worker 单例池,避免重复 loadPyodide() (3-15s/次)
// V2: preflight 仍走池（不需要物理隔离），战斗期改走 PyodideDualRuntime
import { acquireWorker, releaseWorker } from "./pyodide-pool";
// V2: 双 Worker 物理隔离运行时（ADR-002 方案 B）
import { PyodideDualRuntime, BattleError } from "./pyodide-dual-runtime";
// FR-018 ADR-018.1:tank 数值单一真相源(HP/DMG/Range/CD/vision/accuracy)
// Phase 3: chebyshevDistance 已迁移到 core/ + referee/ 子系统
import { TANK_STATS } from "./tank-stats";
// FR-018 ADR-018.8:cover positions 从 battle_easy.json 自动提取
// isObstacleAt 已迁移到 core/movement-system.ts（Phase 2）
// Phase 2: core/ 子系统（从 engine-scheduler 抽出，行为不变）
import { MovementSystem } from "./core/movement-system";
import { VisibilitySystem } from "./core/visibility-system";
// Phase 3: 裁判系统
import { CombatReferee } from "./referee/combat-referee";
// Phase 4: 从 rules.json 加载规则
import { loadRules } from "./rules-loader";
// V5: 教学关卡任务判定器
import { checkMission, loadMissionConfig, type MissionConfig } from "./mission-judge";
// 2026-07-05:start 阶段绕开池,直接 new(避免 warm worker 的 ready 事件被吞)
// FR-018 ADR-018.1:引擎从 TANK_STATS 读 tank 数值,不再 hardcode TANK_RANGE/DAMAGE/CD
// 双方 tank 共享同一套 TANK_ATTACK_COOLDOWN
// v1.0.1 修订（2026-07-05）：AI 调参已从 ai_easy_aggressive.json stats 中删除（reaction/aim_accuracy/scan_frequency），
//                            AI 难度改靠 rules 集设计（ai-bot.py 4 条 rule）。引擎硬编码常量同步为 module-level 常量注入。
const MAX_BATTLE_TICKS = 900; // 90s @ 10Hz,死循环安全上限 (V14: 30Hz→10Hz)

// ===== Worker 句柄封装 =====
interface BotWorkerHandle {
  botId: string;
  worker: Worker;
  // ★ P0-#4 修复:rebuild 时复用 botCode/isAI 重新初始化新 Worker
  // 之前 rebuildWorker 只 terminate,新 worker 永不创建,handle.worker 指向已 terminate 的 dead worker
  botCode: string;
  isAI: boolean;
  initDone: boolean;
  initError: string | null;
  pendingResolves: Map<number, (result: BotTickResult) => void>;
  runtimeErrorCount: number;
  lastAction: BotAction;
}

export class EngineScheduler {
  private config: EngineConfig;
  private listeners: Set<EngineEventListener> = new Set();

  // 双 Worker（demo 兼容保留，V2 战斗期改用 dualRuntime）
  private playerHandle: BotWorkerHandle | null = null;
  private aiHandle: BotWorkerHandle | null = null;

  // V2: 双 Worker 物理隔离运行时（ADR-002 方案 B）
  private dualRuntime: PyodideDualRuntime | null = null;
  /** V2 战斗期缓存的 AI code（用于 Worker B 重建时重新注入）— 由 dualRuntime 管理 */

  // Phase 3: 裁判系统
  private referee: CombatReferee | null = null;
  // Phase 4: 当前关卡 ID（用于加载 rules.json）
  private levelId: string = "battle_easy";
  // V5: 教学关卡任务配置（非 V5 关卡为 null）
  private missionConfig: MissionConfig | null = null;

  // tick 状态
  private tickNum: number = 0;
  private tickTimer: number | null = null;
  private isRunning: boolean = false;
  private isPaused: boolean = false;
  private speedMultiplier: number = 1; // 屏 4 速度控制（1x/2x/4x/pause）
  private battleState: BattleState | null = null;

  // P0-#9:屏 5.5 关键事件流状态(GDD §13.2.4)
  // FIRST_BLOOD 一局只触发一次,COVER_USE 一局可能多次,DEATH 一局最多 2 次
  private firstBloodEmitted: boolean = false;
  private playerUsedCover: boolean = false; // 玩家是否曾经到过 cover 位置
  private aiUsedCover: boolean = false;

  // 硬超时 watchdog
  private hardTimeoutWatchdog: number | null = null;

  // 2026-07-12: headless 模式标记 — Arena 批量战斗时抑制细粒度日志
  private headlessMode: boolean = false;
  /** Arena headless 模式：抑制 [STEP]/[STEP_BLOCKED]/[BC-DIAG] 等细粒度日志 */
  setHeadlessMode(enabled: boolean): void {
    this.headlessMode = enabled;
    MovementSystem.setHeadlessMode(enabled);
  }

  // 真实伤害系统：1v1 模型下 enemy 没有 cooldown_left 字段，单独维护
  // FR-018 ADR-P2.2:统一双方 cooldown 维护路径(都是 self.cooldown_left,镜像注入到 AI 视角)
  // V3: 多坦克模式下 cooldown 由 CombatReferee.tickCooldowns() 统一维护
  private _enemyCooldownLeft: number = 0;

  // V3: 每队坦克数（1=1v1, 2=2v2, 3=3v3）
  private unitsPerTeam: number = 1;

  // V3: 坦克间消息队列 — messageInboxes["team_a"][tankIndex] = [{from, data}, ...]
  private messageInboxes: Map<string, Map<number, Array<{ from: number; data: any }>>> = new Map();

  /** V3: 设置多坦克模式（在 start() 前调用） */
  setUnitsPerTeam(n: number): void {
    this.unitsPerTeam = Math.max(1, Math.min(3, n));
  }


  constructor(config: Partial<EngineConfig> = {}) {
    this.config = { ...DEFAULT_ENGINE_CONFIG, ...config };
  }

  // ===== 公共 API =====
  on(listener: EngineEventListener) {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: EngineEvent) {
    // 隔离单个 listener 异常:任一 listener 抛错不再中断事件分发,
    // 且错误必落 console.error → browser.log。
    // 背景(2026-07-08):屏 4 listener 的 loading_progress 分支曾因 CONSOLE 状态块
    //   声明晚于 engine.start 触发 TDZ,异常经此 forEach 冒泡到 init 回调,被包装成
    //   "Worker init failed",既炸穿引擎又不进日志。这里做安全网,防同类问题复发。
    this.listeners.forEach((l) => {
      try {
        l(event);
      } catch (err) {
        console.error(`[ENGINE] listener threw on '${event.type}' event:`, err);
      }
    });
  }

  /**
   * 屏 2 调用的 Pre-flight：跑 5 tick mini scenario 验证玩家 bot
   * 同步也跑 AI bot preflight（共用同一组 PRE_FLIGHT_TICKS）
   * 返回合并结果 — 双方都通过才算 passed
   *
   * 2026-07-06:加 opts.onLog/onPhase 回调,屏 2 preflight log panel 实时显示 stdout
   */
  async preflight(playerCode: string, opts?: PreflightOptions, mapId?: string): Promise<PreFlightResult> {
    const [playerResult, aiResult] = await Promise.all([
      this.runSinglePreflight(playerCode, false, "preflight_player", opts, mapId),
      this.runSinglePreflight(AI_BOT_CODE, true, "preflight_ai", opts, mapId),
    ]);

    // 合并结果
    const merged: PreFlightResult = {
      passed: playerResult.passed && aiResult.passed,
      total_duration_ms: Math.max(playerResult.total_duration_ms, aiResult.total_duration_ms),
      perTick: playerResult.perTick, // 用 player 的 perTick 作为主要输出
      errors: [
        ...playerResult.errors.map((e) => `[player] ${e}`),
        ...aiResult.errors.map((e) => `[ai] ${e}`),
      ],
    };
    return merged;
  }

  /**
   * 跑单个 bot 的 5-tick preflight（player / AI 共用）
   *
   * 2026-07-06:加 opts 参数转发到 stdout/stderr listener
   *  - 装上 logListener(只听 stdout/stderr,tick_result 仍由 runPreFlightTick 内部 handler 处理)
   *  - logListener 与 runPreFlightTick 互不冲突:addEventListener 多次挂,removeEventListener 单次摘
   */
  private async runSinglePreflight(
    botCode: string,
    isAI: boolean,
    botId: string,
    opts?: PreflightOptions,
    mapId?: string,
  ): Promise<PreFlightResult> {
    // P1-#4:从 PyodidePool 取 worker(优先取热 worker,省 loadPyodide 时间)
    const worker = acquireWorker();
    const t0 = performance.now();
    const perTick: PreFlightResult["perTick"] = [];
    const errors: string[] = [];

    // 2026-07-06:装 log listener(只听 stdout/stderr,不动 tick_result 路径)
    const logListener = opts?.onLog
      ? (e: MessageEvent<WorkerOutbound>) => {
          const msg = e.data;
          if (msg.type === "stdout") {
            opts.onLog!({
              source: isAI ? "ai" : "player",
              level: "info",
              message: msg.chunk,
              ts: performance.now(),
            });
          } else if (msg.type === "stderr") {
            opts.onLog!({
              source: isAI ? "ai" : "player",
              level: "error",
              message: msg.chunk,
              ts: performance.now(),
            });
          }
        }
      : null;
    if (logListener) worker.addEventListener("message", logListener);

    try {
      // 加载 Pyodide
      await this.initPyodideInWorker(worker);

      // 注入 bot-api + bot code
      worker.postMessage({
        type: "init",
        botCode,
        isAI,
        botId,
      } as WorkerInbound);

      // 等 init 完成
      const initOk = await this.waitForInit(worker, 5000);
      if (!initOk) {
        releaseWorker(worker, false); // init 失败,worker 不健康
        return {
          passed: false,
          total_duration_ms: performance.now() - t0,
          perTick: [],
          errors: ["bot init timeout or Pyodide load failed"],
        };
      }

      // 跑 5 tick
      // FR-025:屏 2 选哪张图,preflight 就用哪张图的 spawn/cover,默认 battle_easy
      const tickDefs = mapId ? makePreFlightTicks(mapId) : PRE_FLIGHT_TICKS;
      let totalDuration = 0;
      let allPassed = true;
      for (const def of tickDefs) {
        // 2026-07-06:每次 tick 完成后推一条"tick 完成"engine log(供屏 2 看到节奏)
        const tickResult = await this.runPreFlightTick(worker, def);
        perTick.push(tickResult);
        totalDuration += tickResult.duration_ms;
        // 屏 2 log:tick 编号 + 标签 + 耗时 + action(玩家/AI 视角)
        opts?.onLog?.({
          source: isAI ? "ai" : "player",
          level: tickResult.error ? "error" : "info",
          message: `tick ${def.state.tick} · ${def.label} · ${tickResult.action.type} · ${tickResult.duration_ms.toFixed(1)}ms`,
          ts: performance.now(),
        });
        if (!tickResult.passed) {
          allPassed = false;
          if (tickResult.error) errors.push(`${def.label}: ${tickResult.error}`);
        }
      }

      releaseWorker(worker, true); // pre-flight 跑完,worker 健康,还池
      const finalTotal = performance.now() - t0;

      return {
        passed: allPassed && finalTotal < this.config.pre_flight_max_total_ms, // 2026-07-05: 改 * 2 为不乘,config 已含 init
        total_duration_ms: finalTotal,
        perTick: perTick,
        errors,
      };
    } catch (e: any) {
      releaseWorker(worker, false); // 异常路径,不健康
      return {
        passed: false,
        total_duration_ms: performance.now() - t0,
        perTick,
        errors: [e.message],
      };
    } finally {
      // 2026-07-06:无论成功/异常都摘 log listener(防止 worker 还池后被复用时残留 listener)
      if (logListener) worker.removeEventListener("message", logListener);
    }
  }

  /**
   * 启动战斗：V2 走 PyodideDualRuntime（2 常驻 Worker 物理隔离）
   * 保留 playerCode/aiCode 参数兼容 V2 调用方传入的 aiCode
   */
  async start(playerCode: string | string[], initialState: BattleState, aiCode?: string) {
    if (this.isRunning) return;
    this.battleState = initialState;
    this._enemyCooldownLeft = 0;

    // V3: 从 self/enemy 构建 team_a/team_b 团队状态（1v1 兼容）
    if (!this.battleState.team_a) {
      this.battleState.team_a = {
        tanks: [{
          ...this.battleState.self,
          tank_index: 0,
        }],
        units_per_team: 1,
      };
    }
    if (!this.battleState.team_b) {
      this.battleState.team_b = {
        tanks: [{
          team: "team_b" as const,
          tank_index: 0,
          position: { ...this.battleState.enemy.position },
          spawn: { ...this.battleState.enemy.spawn },
          health: this.battleState.enemy.health,
          max_health: this.battleState.enemy.max_health,
          cooldown_left: 0,
          attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
          is_alive: this.battleState.enemy.is_alive,
        }],
        units_per_team: 1,
      };
    }

    // Phase 3+4: 初始化裁判系统（从 rules.json 读取规则配置）
    // V2 默认用 battle_easy 规则，screen-4 可通过 setLevelId 切换
    const rules = loadRules(this.levelId);
    this.referee = new CombatReferee(
      rules.referee,
      { tickNum: 0, emit: (e) => this.emit(e) },
      rules,
    );
    this.unitsPerTeam = rules.constraints?.max_units_per_team ?? 1;

    // V3: 初始化坦克间消息队列（每个队每个 tank 的空收件箱）
    this.messageInboxes.clear();
    for (const team of ["team_a", "team_b"]) {
      const teamInbox = new Map<number, Array<{ from: number; data: any }>>();
      for (let i = 0; i < this.unitsPerTeam; i++) {
        teamInbox.set(i, []);
      }
      this.messageInboxes.set(team, teamInbox);
    }

    // V2: 使用双 Worker 物理隔离运行时（ADR-002 方案 B）
    const effectiveAiCode = aiCode || AI_BOT_CODE;
    this.dualRuntime = new PyodideDualRuntime();

    try {
      await this.dualRuntime.init(playerCode, effectiveAiCode, (msg) => {
        // 发射加载进度事件（屏 4 消费显示进度条）
        this.emit({
          type: "loading_progress",
          stage: msg,
        });
      });
    } catch (e: any) {
      this.cleanup();
      // 让致命 init 错误进 browser.log(console-logger 只 tap console.*),
      // emit(error) 仅供屏 4 UI,不写日志文件
      console.error("[ENGINE] Worker init failed:", e);
      this.emit({
        type: "error",
        source: "engine",
        errorCode: ERROR_CODES.THINK_EXCEPTION,
        message: `Worker init failed: ${e.message}`,
        tickNum: 0,
      });
      return;
    }

    // 若 init 期间被 stop()(如玩家离开屏幕触发 cleanup→engine.stop),
    // dualRuntime 已 dispose 置 null,放弃启动,避免 isRunning 悬挂为 true。
    if (!this.dualRuntime) return;

    this.isRunning = true;
    this.tickNum = 0;
    this.runTickLoop();
  }

  /**
   * 停止战斗
   */
  stop() {
    this.isRunning = false;
    this.isPaused = false;
    if (this.tickTimer !== null) {
      clearTimeout(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.hardTimeoutWatchdog !== null) {
      clearTimeout(this.hardTimeoutWatchdog);
      this.hardTimeoutWatchdog = null;
    }
    // A7: dualRuntime.dispose() 统一由 cleanup() 处理，避免双重释放
    this.cleanup();
  }

  /**
   * 暂停战斗（保留 tickNum 与 worker 状态，恢复时继续）
   * - 不 terminate Worker（v0 demo 简化：暂停期间 bot 计时仍走）
   * - 取消 setTimeout tick loop
   */
  pause() {
    if (!this.isRunning || this.isPaused) return;
    this.isPaused = true;
    if (this.tickTimer !== null) {
      clearTimeout(this.tickTimer);
      this.tickTimer = null;
    }
  }

  /**
   * 恢复战斗（与 pause 配对）
   */
  resume() {
    if (!this.isRunning || !this.isPaused) return;
    this.isPaused = false;
    this.runTickLoop();
  }

  /**
   * 屏 4 速度控制：1/2/4 倍速
   * - 1x: tick 周期 = 1000/tick_rate_hz
   * - 2x: 周期 ÷ 2（更密集调度）
   * - 4x: 周期 ÷ 4
   * - 实现方式：每次 speed 变化立即 cancel 当前 tickTimer，
   *   并在 runTickLoop 读取实时 speedMultiplier
   */
  setSpeedMultiplier(multiplier: number) {
    if (multiplier <= 0) return; // 0 是 pause 的语义，请用 pause()
    this.speedMultiplier = multiplier;
    if (this.isPaused) return; // 暂停状态下不重启
    // 重启 tick loop（立即按新 speed 推进）
    if (this.tickTimer !== null) {
      clearTimeout(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.isRunning) {
      this.runTickLoop();
    }
  }

  /**
   * Phase 4: 设置当前关卡 ID — 用于加载对应的 rules.json。
   * screen-4 在启动战斗前调用此方法。
   */
  setLevelId(levelId: string): void {
    this.levelId = levelId;
    // V5: 教学关卡加载任务配置（异步，不阻塞）
    if (levelId.startsWith("v5_")) {
      loadMissionConfig(levelId).then((cfg) => {
        this.missionConfig = cfg;
      });
    } else {
      this.missionConfig = null;
    }
  }

  /**
   * ★ P0-#4 修复:抽 onmessage/onerror handler 绑定
   * (rebuild 时需要给新 worker 重新挂同一组 handlers)
   */
  private attachWorkerHandlers(worker: Worker, handle: BotWorkerHandle): void {
    const { botId, botCode, isAI } = handle;
    worker.onmessage = (e: MessageEvent<WorkerOutbound>) => {
      const msg = e.data;
      switch (msg.type) {
        case "ready":
          // Worker 加载完 Pyodide，可以发 init
          worker.postMessage({ type: "init", botCode, isAI, botId } as WorkerInbound);
          break;
        case "init_done":
          handle.initDone = true;
          handle.initError = msg.error;
          break;
        case "tick_result":
          {
            const resolver = handle.pendingResolves.get(msg.tickNum);
            if (resolver) {
              handle.pendingResolves.delete(msg.tickNum);
              resolver(msg.result);
            }
            handle.lastAction = msg.result.action;
            if (msg.result.error) {
              this.handleRuntimeError(handle, msg.result.error, msg.tickNum);
            }
          }
          break;
        case "error":
          this.handleRuntimeError(handle, msg.error, -1);
          break;
        // 2026-07-05 T47.6:worker 内部 diag_log → 主线程 console (已被 console-logger 拦截)
        // 2026-07-11:info 级 diag_log 不再转发(每 tick 6+ 条 [BC-DIAG] 导致 browser.log 膨胀),
        //   仅保留 warn/error 级(init 失败/tick 异常等关键事件)
        case "diag_log": {
          if (msg.level === "info") break; // 抑制 info 级诊断日志(每 tick 高频噪音)
          const m = `[worker:${msg.botId}] ${msg.msg}`;
          if (msg.level === "warn") console.warn(m);
          else if (msg.level === "error") console.error(m);
          break;
        }
      }
    };

    worker.onerror = (e: ErrorEvent) => {
      console.error(`[${botId}] worker error:`, e);
      this.handleRuntimeError(handle, e.message || "worker crashed", -1);
    };
  }

  private async initPyodideInWorker(worker: Worker): Promise<void> {
    // 2026-07-05 修复(P0-#12 后续):warm worker(池里取出的)Pyodide 已就绪,跳过 ready 等待
    // 否则 initPyodideInWorker 永远等不到 ready 事件 → 30s timeout
    if ((worker as any).__pyodideReady === true) {
      return; // warm worker,直接 resolve
    }
    // 2026-07-05 缩短:30s → 15s。本地化后 cold start 应 < 2s,15s 失败必是配置/网络问题
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => reject(new Error("Pyodide init timeout (本地 + CDN 15s 全失败)")), 15000);
      const handler = (e: MessageEvent<WorkerOutbound>) => {
        if (e.data.type === "ready") {
          clearTimeout(timer);
          worker.removeEventListener("message", handler);
          resolve();
        } else if (e.data.type === "error" && e.data.phase === "init") {
          // 2026-07-05 修复(诊断静默三连之二):worker 显式报 init error(CDN 全部失败等)
          // 之前 handler 只看 ready,init error 消息被忽略 → 30s timeout 兜底,玩家只看到"init timeout"
          // 现在直接 reject 真实错误信息
          clearTimeout(timer);
          worker.removeEventListener("message", handler);
          reject(new Error(e.data.error));
        }
      };
      worker.addEventListener("message", handler);
    });
  }

  private async waitForInit(worker: Worker, timeoutMs: number): Promise<boolean> {
    // 2026-07-05 修复(诊断静默三连之三):waitForInit 漏处理 error/ready 消息
    //   之前只判 type === "init_done",init 阶段 worker postMessage {type:"error", phase:"init"} 被忽略
    //   → 5s timeout 一到 resolve(false) → 主线程"bot init timeout",但实际是 CDN 失败
    //   修复:增 error 分支立即 resolve(false)(init 失败),ready 视为 init 完成(resolve(true))
    return new Promise((resolve) => {
      const handler = (e: MessageEvent<WorkerOutbound>) => {
        if (e.data.type === "init_done") {
          worker.removeEventListener("message", handler);
          clearTimeout(timer);
          resolve(e.data.error === null);
        } else if (e.data.type === "error" && e.data.phase === "init") {
          // init 阶段报错:立即判定为失败,不等 timeout
          worker.removeEventListener("message", handler);
          clearTimeout(timer);
          console.error("[BC-DIAG] waitForInit received init error:", e.data.error);
          resolve(false);
        } else if (e.data.type === "ready") {
          // Pyodide runtime 就绪但 init(bot code)未完成 — 视为"运行时 OK",
          // init_done 才是真正完成,所以这里不 resolve,继续等
        }
      };
      const timer = setTimeout(() => {
        worker.removeEventListener("message", handler);
        console.error(`[BC-DIAG] waitForInit timeout after ${timeoutMs}ms (no init_done message)`);
        resolve(false);
      }, timeoutMs);
      worker.addEventListener("message", handler);
    });
  }

  private async runPreFlightTick(worker: Worker, def: PreFlightTickDef) {
    return new Promise<PreFlightResult["perTick"][number]>((resolve) => {
      const handler = (e: MessageEvent<WorkerOutbound>) => {
        if (e.data.type === "tick_result") {
          worker.removeEventListener("message", handler);
          const r = e.data.result;
          resolve({
            tick: def.state.tick,
            label: def.label,
            // 2026-07-06 P1-#6:透传玩家语言 label(屏 2 5 tick 主信息流展示用)
            playerLabel: def.playerLabel,
            // 2026-07-06 P1-#6:透传场景描述(屏 2 5 tick card hover title 用)
            description: def.description,
            // 2026-07-05 修复(P0-#12 后续):preflight 只验证"不抛异常",不验证 action 类型
            // 之前用"非 idle 才通过"导致 example bot L1/L2/L3 写"冷却中可 idle"时被误判 fail
            // 且屏 2 列表不显示 r.error 字段(实际有显示但 detailEl 渲染时 t.passed=false 不展示)
            // v0.6.0 §5.1 "不靠兜底,要可靠" → 失败要暴露行号+异常类型,行为留给战斗中观察
            passed: !r.error,
            duration_ms: r.duration_ms,
            action: r.action,
            error: r.error,
          });
        }
      };
      worker.addEventListener("message", handler);
      worker.postMessage({ type: "tick", state: def.state, tickNum: def.state.tick } as WorkerInbound);
    });
  }

  // ===== 内部：30Hz tick 循环 =====
  private runTickLoop() {
    if (!this.isRunning || this.isPaused) return;
    const tickIntervalMs = 1000 / this.config.tick_rate_hz / this.speedMultiplier; // 30Hz × speedMult

    const tickFn = async () => {
      // V2: dualRuntime 存在时不需要 playerHandle/aiHandle（demo 兼容路径仍需要）
      if (!this.isRunning || this.isPaused || !this.battleState) return;
      if (!this.dualRuntime && (!this.playerHandle || !this.aiHandle)) return;

      const currentTick = this.tickNum;
      this.battleState.tick = currentTick;

      // Phase 3: 委托给裁判检查 timeout 条件（提前判定，避免无意义 tick）
      if (this.referee) {
        const timeoutResult = this.referee.checkCondition(this.battleState, currentTick, MAX_BATTLE_TICKS);
        if (timeoutResult && timeoutResult.type === "draw") {
          this.endBattle("draw", timeoutResult.reason);
          return;
        }
      }

      try {
        // Q4=B Promise.all 并行调用双 bot
        // 关键：AI worker 需要的是"AI 视角"（self=AI, enemy=player），
        // 而 this.battleState 是"玩家视角"（self=player, enemy=AI）。
        // 所以要给 AI worker 构造一个镜像 state。
        const aiViewState = this.mirrorStateForAI(this.battleState);

        // V2: 通过 PyodideDualRuntime 并行执行双方 think
        let playerResult: BotTickResult;
        let aiResult: BotTickResult;
        /** V3: 多坦克模式标记（tick 循环后续分支用） */
        let isMultiTick = this.unitsPerTeam > 1 && !!this.battleState.team_a && !!this.battleState.team_b;

        if (this.dualRuntime) {
          if (isMultiTick) {
            // V3 多坦克路径：发送 team 状态，Worker 内遍历坦克 think()
            // 注入上 tick 的路由消息到各自收件箱
            const playerInbox = this.messageInboxes.get("team_a") 
              ? Object.fromEntries(this.messageInboxes.get("team_a")!.entries()) 
              : {};
            const aiInbox = this.messageInboxes.get("team_b")
              ? Object.fromEntries(this.messageInboxes.get("team_b")!.entries())
              : {};
            const [pResult, aResult] = await Promise.all([
              this.dualRuntime.runTeamThink("player", this.battleState, currentTick, playerInbox),
              this.dualRuntime.runTeamThink("ai", aiViewState, currentTick, aiInbox),
            ]);
            playerResult = pResult;
            aiResult = aResult;
          } else {
            // V2 1v1 路径：双 Worker 物理隔离
            const both = await this.dualRuntime.runBothThink(
              this.battleState,
              aiViewState,
              currentTick,
            );
            playerResult = both.player;
            aiResult = both.ai;
          }

          // 异常检测：单方连续 3 次异常 → 终止战斗
          this.checkDualRuntimeErrors(currentTick);
        } else {
          // demo 兼容路径：直接用 BotWorkerHandle
          const [pResult, aResult] = await Promise.all([
            this.callBotWithTimeouts(this.playerHandle!, currentTick, this.battleState),
            this.callBotWithTimeouts(this.aiHandle!, currentTick, aiViewState),
          ]);
          playerResult = pResult;
          aiResult = aResult;
        }

        // ===== V3: 多坦克路径 vs 1v1 路径 =====
        if (isMultiTick) {
          this.processMultiTankTick(currentTick, playerResult, aiResult);
          // V3: 坦克间消息路由 — 读取本 tick outbox，写入下 tick 目标收件箱
          this.routeTankMessages("team_a", playerResult.message_outbox);
          this.routeTankMessages("team_b", aiResult.message_outbox);
          // V5: 教学关卡任务判定（优先于通用裁判）
          if (this.missionConfig) {
            const missionResult = checkMission(this.battleState, this.missionConfig);
            if (missionResult.fail) {
              this.endBattle("team_b", missionResult.reason);
              return;
            }
            if (missionResult.win) {
              this.endBattle("team_a", missionResult.reason);
              return;
            }
          }
          // 多坦克裁判判定胜负
          if (this.referee) {
            const condition = this.referee.checkCondition(this.battleState, currentTick, MAX_BATTLE_TICKS);
            if (condition && condition.type === "win") {
              this.endBattle(condition.team, condition.reason);
              return;
            }
          }
        } else {
          // ===== 1v1 路径：保持原有逻辑不变 =====
          // Phase 4: validateAction 预校验
          playerResult.action = this.validateAndFilterAction("team_a", playerResult.action, currentTick);
          aiResult.action = this.validateAndFilterAction("team_b", aiResult.action, currentTick);

          // 应用 move action
          this.applyMoveAction("team_a", playerResult.action);
          this.applyMoveAction("team_b", aiResult.action);
          this.applyScanAction(playerResult.action, aiResult.action);
          this.applyVisibilityByDistance();

          if (!this.headlessMode && currentTick < 30 && currentTick % 5 === 0) {
            console.info(`[BC-DIAG] main tick=${currentTick} player.action=${JSON.stringify(playerResult.action)} self.pos=${JSON.stringify(this.battleState.self.position)} enemy.pos=${JSON.stringify(this.battleState.enemy.position)}`);
          }

          // 真实伤害结算
          const damageFromA = this.resolveDamage("team_a", playerResult.action);
          const damageFromB = this.resolveDamage("team_b", aiResult.action);

          if (damageFromA > 0) this.battleState.enemy.health = Math.max(0, this.battleState.enemy.health - damageFromA);
          if (damageFromB > 0) this.battleState.self.health = Math.max(0, this.battleState.self.health - damageFromB);

          // cooldown
          if (this.battleState.self.cooldown_left > 0) this.battleState.self.cooldown_left -= 1;
          if (this._enemyCooldownLeft > 0) this._enemyCooldownLeft -= 1;

          // hit 事件
          if (damageFromA > 0) {
            if (!this.firstBloodEmitted) {
              this.firstBloodEmitted = true;
              this.emit({ type: "key_event", eventType: "FIRST_BLOOD", tickNum: currentTick, actor: "team_a", description: "玩家先手命中" });
            }
            this.emit({ type: "hit", tickNum: currentTick, shooter: "team_a", target: "team_b", damage: damageFromA, killed: this.battleState.enemy.health <= 0 });
            this.emit({ type: "key_event", eventType: "COOLDOWN_TRIGGER", tickNum: currentTick, actor: "team_a", description: "玩家开火后进入冷却" });
          }
          if (damageFromB > 0) {
            if (!this.firstBloodEmitted) {
              this.firstBloodEmitted = true;
              this.emit({ type: "key_event", eventType: "FIRST_BLOOD", tickNum: currentTick, actor: "team_b", description: "AI 先手命中" });
            }
            this.emit({ type: "hit", tickNum: currentTick, shooter: "team_b", target: "team_a", damage: damageFromB, killed: this.battleState.self.health <= 0 });
            this.emit({ type: "key_event", eventType: "COOLDOWN_TRIGGER", tickNum: currentTick, actor: "team_b", description: "AI 开火后进入冷却" });
          }

          if (this.battleState.enemy.health <= 0) {
            this.emit({ type: "key_event", eventType: "DEATH", tickNum: currentTick, actor: "team_b", description: "AI 阵亡" });
          }
          if (this.battleState.self.health <= 0) {
            this.emit({ type: "key_event", eventType: "DEATH", tickNum: currentTick, actor: "team_a", description: "玩家阵亡" });
          }

          // 同步 team 状态 + emit tick
          this.syncTeamState();
          this.emit({
            type: "tick", tickNum: currentTick,
            state: JSON.parse(JSON.stringify(this.battleState)) as BattleState,
            actions: { team_a: playerResult.action, team_b: aiResult.action },
            logs: { team_a: playerResult.logs, team_b: aiResult.logs },
            damage: { team_a_to_team_b: damageFromA, team_b_to_team_a: damageFromB },
          });

          // V5: 教学关卡任务判定（优先于通用裁判）
          if (this.missionConfig) {
            const missionResult = checkMission(this.battleState, this.missionConfig);
            if (missionResult.fail) {
              this.endBattle("team_b", missionResult.reason);
              return;
            }
            if (missionResult.win) {
              this.endBattle("team_a", missionResult.reason);
              return;
            }
          }
          // 1v1 裁判判定
          if (this.referee) {
            const condition = this.referee.checkCondition(this.battleState, currentTick, MAX_BATTLE_TICKS);
            if (condition && condition.type === "win") {
              this.endBattle(condition.team, condition.reason);
              return;
            }
          }
        }
      } catch (e: any) {
        // A6: BattleError → 根据来源终止战斗
        if (e instanceof BattleError) {
          if (e.source === "player") {
            this.endBattle("team_b", `玩家 Worker 连续异常: ${e.message}`);
          } else if (e.source === "ai") {
            this.endBattle("team_a", `AI Worker 连续异常: ${e.message}`);
          } else {
            this.endBattle("draw", `双方 Worker 连续异常: ${e.message}`);
          }
          return;
        }
        this.emit({ type: "error", source: "engine", errorCode: ERROR_CODES.TICK_LOOP_CORRUPTED, message: e.message, tickNum: currentTick });
      }

      this.tickNum++;
      this.tickTimer = window.setTimeout(tickFn, tickIntervalMs);
    };

    tickFn();
  }

  // ===== 镜像 state 给 AI =====
  // AI bot 的 self API 跟玩家完全对称（demo-gdd §6.1.0），
  // 所以给 AI 的 state 必须是 "self=AI, enemy=player" 的视角。
  // 玩家视角的 state（self=player, enemy=AI）需要镜像。
  // 注意：这不是深拷贝，是浅拷贝 + 重建 self/enemy 子对象（避免双 bot 共享引用导致 state 污染）
  private mirrorStateForAI(playerView: BattleState): BattleState {
    const mirrored: BattleState = {
      tick: playerView.tick,
      map_id: playerView.map_id,
      map_size: { ...playerView.map_size },
      nearest_cover: playerView.nearest_cover
        ? {
            position: { ...playerView.nearest_cover.position },
            distance: playerView.nearest_cover.distance,
          }
        : playerView.nearest_cover,
      // 镜像：self ← enemy（AI 视角，self=AI）
      self: {
        team: "team_b",
        tank_index: 0,
        position: { ...playerView.enemy.position },
        spawn: { ...playerView.enemy.spawn },
        health: playerView.enemy.health,
        max_health: TANK_STATS.max_health,
        cooldown_left: this._enemyCooldownLeft,
        attack_cooldown_ticks: TANK_STATS.attack_cooldown_ticks,
        is_alive: playerView.enemy.is_alive,
      },
      // 镜像：enemy ← self（AI 视角，enemy=player）
      enemy: {
        tank_index: 0,
        position: { ...playerView.self.position },
        spawn: { ...playerView.self.spawn },
        health: playerView.self.health,
        max_health: TANK_STATS.max_health,
        is_alive: playerView.self.is_alive,
        is_visible: playerView.enemy.is_visible,
        last_known_position: playerView.ai_known_player_position
          ? { ...playerView.ai_known_player_position }
          : null,
      },
      // V3: 镜像 team 状态（AI 视角：team_a=AI队, team_b=玩家队）
      team_a: playerView.team_b
        ? { tanks: playerView.team_b.tanks.map((t) => ({ ...t, position: { ...t.position }, spawn: { ...t.spawn } })), units_per_team: playerView.team_b.units_per_team }
        : undefined,
      team_b: playerView.team_a
        ? { tanks: playerView.team_a.tanks.map((t) => ({ ...t, position: { ...t.position }, spawn: { ...t.spawn } })), units_per_team: playerView.team_a.units_per_team }
        : undefined,
    };
    return mirrored;
  }

  // ===== 应用 action（move → position, scan → is_visible）=====
  // AI bot 4 rules 真实运行的关键：把双方 bot 的 action 应用到物理世界
  /**
   * Phase 2: 委托给 MovementSystem.applyMove（行为不变）
   * 原逻辑从 engine-scheduler.ts 迁移到 core/movement-system.ts
   * GDD §12.3 LOCK 7 档碰撞规则 + COVER_USE 事件
   */
  private applyMoveAction(team: "team_a" | "team_b", action: BotAction): void {
    if (!this.battleState) return;
    // 2026-07-12: headless 模式下 MovementSystem 自动抑制 STEP/STEP_BLOCKED 日志
    const flags = MovementSystem.applyMove(
      this.battleState,
      team,
      action,
      { tickNum: this.tickNum, emit: (e) => this.emit(e) },
      { playerUsedCover: this.playerUsedCover, aiUsedCover: this.aiUsedCover },
    );
    this.playerUsedCover = flags.playerUsedCover;
    this.aiUsedCover = flags.aiUsedCover;
  }

  /**
   * Phase 2: 委托给 VisibilitySystem.applyScan（行为不变）
   * 原逻辑从 engine-scheduler.ts L908-975 迁移到 core/visibility-system.ts
   */
  private applyScanAction(playerAction: BotAction, aiAction: BotAction): void {
    if (!this.battleState) return;
    VisibilitySystem.applyScan(
      this.battleState,
      playerAction,
      aiAction,
      { tickNum: this.tickNum, emit: (e) => this.emit(e) },
    );
  }

  /**
   * FR-022 v1.0.3 修订(2026-07-05):is_visible 改为每 tick 派生量
   *   - dist ≤ vision_range && 双方 alive → is_visible = true(自动发现,无需主动 scan)
   *   - dist > vision_range → is_visible = false
   *   - 死人 is_visible = false(死人眼不存在)
   *   - 不 emit 事件(避免每 tick 都打"看见/看不见",只在 SCAN_DISCOVERY 关键节点 emit)
   *   - scan 改为只写 last_known_position(详见 applyScanAction L801 注释)
   */
  private applyVisibilityByDistance(): void {
    if (!this.battleState) return;
    // Phase 2: 委托给 VisibilitySystem（行为不变）
    VisibilitySystem.applyByDistance(this.battleState);
  }

  // ===== Phase 4: 动作预校验（rules.json blocked_apis 拦截）=====
  // 在 tick 循环中，双方 think() 返回 action 后、应用 action 前调用
  // 若 action 被 rules.json blocked_apis 禁用 → 替换为 idle + emit key_event 通知 UI
  // V2 不做"中断战斗"惩罚，仅反馈 + 跳过
  private validateAndFilterAction(
    team: "team_a" | "team_b",
    action: BotAction,
    tickNum: number,
  ): BotAction {
    if (!this.referee || !this.battleState) return action;
    const result = this.referee.validateAction(action, this.battleState, team);
    if (result.ok) return action;

    // blocked → 替换为 idle，emit 事件通知 UI
    console.warn(`[ENGINE] ${team} action blocked: ${result.message} (tick ${tickNum})`);
    this.emit({
      type: "key_event",
      eventType: "ACTION_BLOCKED",
      tickNum,
      actor: team,
      description: `${team} action '${action.type}' blocked: ${result.message}`,
    });
    return { type: "idle" } as BotAction;
  }

  // ===== 真实伤害结算 =====
  // Phase 3: 委托给 CombatReferee.resolveDamage（行为不变）
  // 原逻辑从 engine-scheduler.ts L840-903 迁移到 referee/combat-referee.ts
  //
  // 规则:
  //   1) 仅当 action.type === "shoot" 时才可能造成伤害
  //   2) cooldown_left 必须 == 0（shoot 后置为 attack_cooldown_ticks）
  //   3) Chebyshev 距离 ≤ tank.range 内才能开火（超距 emit SHOOT_FAILED）
  //   4) 每次攻击按 accuracy roll 一次随机数决定命中
  //   5) 命中则扣 DMG；未命中仍消耗 cooldown（开火动作发生了）
  // 返回 0 = 未造成伤害；返回 damage = 命中
  private resolveDamage(
    shooterTeam: "team_a" | "team_b",
    action: BotAction
  ): number {
    if (!this.battleState || !this.referee) return 0;
    // 同步 referee 的 enemyCooldownLeft
    this.referee.setEnemyCooldownLeft(this._enemyCooldownLeft);
    const damage = this.referee.resolveDamage(shooterTeam, action, this.battleState);
    // 回读 referee 可能修改的 enemyCooldownLeft
    this._enemyCooldownLeft = this.referee.getEnemyCooldownLeft();
    return damage;
  }

  private callBotWithTimeouts(handle: BotWorkerHandle, tickNum: number, state: BattleState): Promise<BotTickResult> {
    return new Promise((resolve) => {
      // ★ P0-#4 修复:rebuild 后新 worker 还没 init_done,本 tick 不能 postMessage
      // 否则 tick 消息会被未就绪 worker 丢弃 → pendingResolves 永不 resolve → 卡死
      if (!handle.initDone) {
        resolve({ action: { type: "idle" }, logs: [`[worker not ready]`], duration_ms: 0, error: null, timed_out: false });
        return;
      }

      // Q5=B 硬超时 watchdog（200ms 后 worker.terminate + 重建）
      const watchdog = window.setTimeout(() => {
        console.warn(`[${handle.botId}] hard timeout @ tick ${tickNum}, terminating worker`);
        handle.pendingResolves.delete(tickNum);
        this.rebuildWorker(handle);
        resolve({ action: { type: "idle" }, logs: [`[hard timeout]`], duration_ms: this.config.hard_timeout_ms, error: "hard_timeout", timed_out: true });
      }, this.config.hard_timeout_ms);

      handle.pendingResolves.set(tickNum, (result) => {
        clearTimeout(watchdog);
        // Q5=A 软超时标记
        if (result.duration_ms > this.config.soft_timeout_ms) {
          result.timed_out = true;
        }
        resolve(result);
      });

      handle.worker.postMessage({ type: "tick", state, tickNum } as WorkerInbound);
    });
  }

  /**
   * ★ P0-#4 修复:rebuildWorker 真重建
   * 之前只 terminate() 不重建 → handle.worker 指向 dead worker → 下一 tick
   * postMessage 静默丢消息 → pendingResolves 永不 resolve → 战斗永久卡死
   * 现在:terminate + new PyodideWorker + 重新挂 handlers + 重新发 init
   * 旧 handle 上的 botCode/isAI/pendingResolves/runtimeErrorCount/lastAction 全部保留
   * 累计 rebuild 次数算 1 次 runtime error(沿用 GDD §11.6.5 设计)
   */
  private rebuildWorker(handle: BotWorkerHandle) {
    // 1) 杀旧 worker
    releaseWorker(handle.worker, false); // 已被 watchdog 标记不健康,不还池

    // 2) 创建新 worker
    const newWorker = acquireWorker();

    // 3) 挂同一组 handlers(handle 上 botCode/isAI/botId 不变)
    this.attachWorkerHandlers(newWorker, handle);

    // 4) 替换 worker 引用
    handle.worker = newWorker;

    // 5) 重置 init 状态 — 新 worker 必须重新 init 完成才能接 tick
    handle.initDone = false;
    handle.initError = null;

    // 6) 累计 1 次 runtime error,GDD §11.6.5 L1253 "连续 5 tick WARN 触发硬超时"
    //    达到 runtime_error_threshold(=3) → endBattle draw
    handle.runtimeErrorCount++;
    if (handle.runtimeErrorCount >= this.config.runtime_error_threshold) {
      this.endBattle("draw", `${handle.botId} exceeded error threshold (${this.config.runtime_error_threshold})`);
    }

    console.warn(`[${handle.botId}] worker rebuilt (runtimeErrorCount=${handle.runtimeErrorCount})`);
  }

  private handleRuntimeError(handle: BotWorkerHandle, error: string, tickNum: number) {
    handle.runtimeErrorCount++;
    // P0-#8 修复:用 GDD §13.7 锁的 7 个错误码常量(不再随机生成 0x5E10-0x5E69)
    // player think() 抛未捕获异常 / worker error → 0x5E03
    // AI worker error → 0x5E10
    const errorCode = handle.isAI ? ERROR_CODES.AI_RULE_INVALID : ERROR_CODES.THINK_EXCEPTION;
    this.emit({
      type: "error",
      source: handle.isAI ? "ai" : "player",
      errorCode,
      message: error,
      tickNum,
    });
    if (handle.runtimeErrorCount >= this.config.runtime_error_threshold) {
      this.endBattle("draw", `${handle.botId} exceeded error threshold (${this.config.runtime_error_threshold}): ${error}`);
    }
  }

  private endBattle(winner: "team_a" | "team_b" | "draw", reason: string) {
    this.isRunning = false;
    if (this.tickTimer !== null) {
      clearTimeout(this.tickTimer);
      this.tickTimer = null;
    }
    // 2026-07-12: 战斗摘要日志 — 1v1 用 self_hp/enemy_hp，多坦克用团队汇总
    if (this.unitsPerTeam > 1 && this.battleState?.team_a && this.battleState?.team_b) {
      const ta = this.battleState.team_a;
      const tb = this.battleState.team_b;
      const aHpSum = ta.tanks.reduce((s, t) => s + t.health, 0);
      const bHpSum = tb.tanks.reduce((s, t) => s + t.health, 0);
      const aAlive = ta.tanks.filter((t) => t.is_alive).length;
      const bAlive = tb.tanks.filter((t) => t.is_alive).length;
      const aDead = ta.tanks.length - aAlive;
      const bDead = tb.tanks.length - bAlive;
      console.info(
        `[BATTLE_END] winner=${winner} reason="${reason}" tick=${this.tickNum} ` +
        `teamA_hp=${aHpSum}/${ta.tanks.length * 150} alive=${aAlive} dead=${aDead} ` +
        `teamB_hp=${bHpSum}/${tb.tanks.length * 150} alive=${bAlive} dead=${bDead}`
      );
    } else {
      console.info(
        `[BATTLE_END] winner=${winner} reason="${reason}" tick=${this.tickNum} ` +
        `self_hp=${this.battleState?.self.health ?? 0} enemy_hp=${this.battleState?.enemy.health ?? 0}`
      );
    }
    this.cleanup();
    this.emit({ type: "battle_end", winner, reason });
  }

  /**
   * V2: 检查双 Worker 运行时异常状态。
   * - 玩家连续 3 次异常 → emit [RUNTIME_ERR]，AI 继续
   * - AI 连续 3 次异常 → emit [AI_RUNTIME_ERR]，玩家继续
   * - 双方都 3 次连续异常 → draw（平局）
   */
  private checkDualRuntimeErrors(currentTick: number): void {
    if (!this.dualRuntime) return;

    const playerHealthy = this.dualRuntime.isWorkerHealthy("player");
    const aiHealthy = this.dualRuntime.isWorkerHealthy("ai");

    if (!playerHealthy) {
      this.emit({
        type: "error",
        source: "player",
        errorCode: ERROR_CODES.THINK_EXCEPTION,
        message: "玩家代码连续异常，Worker A 已重建",
        tickNum: currentTick,
      });
    }

    if (!aiHealthy) {
      this.emit({
        type: "error",
        source: "ai",
        errorCode: ERROR_CODES.AI_RULE_INVALID,
        message: "AI 代码连续异常，Worker B 已重建",
        tickNum: currentTick,
      });
    }

    if (!playerHealthy && !aiHealthy) {
      // 双方都崩了 → 平局
      this.endBattle("draw", "双方 Worker 连续异常");
    }
  }

  // ===== V3: 坦克间消息路由 =====
  /** 将本 tick 的 outbox 消息投递到目标坦克的下 tick 收件箱。
   * 消息在本队内传递（队友间通信），FIFO 上限 10 条。 */
  private routeTankMessages(team: string, outbox?: Array<Array<{ to: number; data: any }>>): void {
    if (!outbox || outbox.length === 0) return;
    const teamInbox = this.messageInboxes.get(team);
    if (!teamInbox) return;

    for (let fromIdx = 0; fromIdx < outbox.length; fromIdx++) {
      const msgs = outbox[fromIdx];
      if (!msgs || msgs.length === 0) continue;
      for (const msg of msgs) {
        const toIdx = msg.to;
        const target = teamInbox.get(toIdx);
        if (!target) continue;
        target.push({ from: fromIdx, data: msg.data });
        // FIFO 上限：超过 10 条丢弃最旧的
        while (target.length > 10) target.shift();
      }
    }
  }

  // ===== V3: 多坦克 tick 处理（替代 1v1 的 applyMove/resolveDamage 序列）=====
  private processMultiTankTick(
    tickNum: number,
    playerResult: BotTickResult,
    aiResult: BotTickResult,
  ): void {
    if (!this.battleState || !this.referee) return;
    const state = this.battleState;
    const teamA = state.team_a!;
    const teamB = state.team_b!;
    const n = Math.min(this.unitsPerTeam, teamA.tanks.length, teamB.tanks.length);

    // V3 诊断 log：headless 模式下抑制
    if (!this.headlessMode && tickNum === 0) {
      console.info(`[BC-DIAG] processMultiTankTick n=${n} teamA=${teamA.tanks.length} teamB=${teamB.tanks.length}`);
    }

    const playerActions = playerResult.actions ?? [playerResult.action];
    const aiActions = aiResult.actions ?? [aiResult.action];

    // 确保 actions 数组长度足够
    while (playerActions.length < n) playerActions.push({ type: "idle" });
    while (aiActions.length < n) aiActions.push({ type: "idle" });

    const teamADamage: number[] = new Array(n).fill(0);
    const teamBDamage: number[] = new Array(n).fill(0);
    const allPlayerLogs: string[] = playerResult.logs ?? [];
    const allAiLogs: string[] = aiResult.logs ?? [];
    const playerTankLogs: string[][] = playerResult.tank_logs ?? [];
    const aiTankLogs: string[][] = aiResult.tank_logs ?? [];

    // ===== 1. 应用 move action（每个存活坦克独立移动）=====
    for (let i = 0; i < n; i++) {
      const tank = teamA.tanks[i];
      if (!tank.is_alive) continue;
      const action = playerActions[i];
      if (action.type !== "move") continue;

      // 基本边界 + 碰撞检测
      const target = action.target;
      const dx = Math.max(-1, Math.min(1, target.x - tank.position.x));
      const dy = Math.max(-1, Math.min(1, target.y - tank.position.y));
      const newX = tank.position.x + dx;
      const newY = tank.position.y + dy;

      // 边界检查
      if (newX < 0 || newX >= state.map_size.width || newY < 0 || newY >= state.map_size.height) continue;

      // 检查目标位置是否有对方坦克（简化碰撞检测）
      const blocked = [...teamA.tanks, ...teamB.tanks].some(
        (t) => t.is_alive && t !== tank && t.position.x === newX && t.position.y === newY,
      );
      if (blocked) continue;

      tank.position = { x: newX, y: newY };
    }

    for (let i = 0; i < n; i++) {
      const tank = teamB.tanks[i];
      if (!tank.is_alive) continue;
      const action = aiActions[i];
      if (action.type !== "move") continue;

      const target = action.target;
      const dx = Math.max(-1, Math.min(1, target.x - tank.position.x));
      const dy = Math.max(-1, Math.min(1, target.y - tank.position.y));
      const newX = tank.position.x + dx;
      const newY = tank.position.y + dy;

      if (newX < 0 || newX >= state.map_size.width || newY < 0 || newY >= state.map_size.height) continue;

      const blocked = [...teamA.tanks, ...teamB.tanks].some(
        (t) => t.is_alive && t !== tank && t.position.x === newX && t.position.y === newY,
      );
      if (blocked) continue;

      tank.position = { x: newX, y: newY };
    }

    // ===== 2. 应用 scan action =====
    // V3: 多坦克 scan 简化 — 检查 team_a/team_b 是否有坦克 scan
    const pScan = playerActions.find((a) => a.type === "scan") ?? { type: "idle" as const };
    const aScan = aiActions.find((a) => a.type === "scan") ?? { type: "idle" as const };
    if (pScan.type === "scan" || aScan.type === "scan") {
      this.applyScanAction(pScan, aScan);
    }

    // FR-027b: 多坦克可见性派生（与 1v1 applyVisibilityByDistance 对齐）
    // 每 tick 自动计算：dist ≤ vision_range(10) && 双方 alive → is_visible = true
    // 先重置所有 tank 的 is_visible
    for (const t of teamA.tanks) t.is_visible = false;
    for (const t of teamB.tanks) t.is_visible = false;
    for (let i = 0; i < n; i++) {
      const tankA = teamA.tanks[i];
      if (!tankA.is_alive) continue;
      for (let j = 0; j < n; j++) {
        const tankB = teamB.tanks[j];
        if (!tankB.is_alive) continue;
        const d = Math.max(Math.abs(tankA.position.x - tankB.position.x), Math.abs(tankA.position.y - tankB.position.y));
        if (d <= TANK_STATS.vision_range) {
          // 双方互相可见
          tankB.is_visible = true;
          tankA.is_visible = true;
        }
      }
    }
    // 向后兼容：self.enemy.is_visible = 任一敌方存活 tank 可见
    const anyBVisible = teamB.tanks.some(t => t.is_alive && t.is_visible);
    const anyAVisible = teamA.tanks.some(t => t.is_alive && t.is_visible);
    if (state.enemy) state.enemy.is_visible = anyBVisible;
    if (state.self) state.self.is_visible = anyAVisible;

    // ===== 3. resolve damage（per-tank shoot → 最近存活敌人）=====
    for (let i = 0; i < n; i++) {
      if (playerActions[i].type !== "shoot") continue;
      const shooter = teamA.tanks[i];
      if (!shooter.is_alive) continue;
      if (this.referee.getTankCooldown("team_a", i) > 0) continue;

      // 找最近存活敌人
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let j = 0; j < n; j++) {
        const t = teamB.tanks[j];
        if (!t.is_alive) continue;
        const d = Math.max(Math.abs(shooter.position.x - t.position.x), Math.abs(shooter.position.y - t.position.y));
        if (d < bestDist) { bestDist = d; bestIdx = j; }
      }
      if (bestIdx < 0 || bestDist > TANK_STATS.range) continue;

      // accuracy roll
      if (Math.random() >= TANK_STATS.player_accuracy) {
        this.referee.setTankCooldown("team_a", i, TANK_STATS.attack_cooldown_ticks);
        continue;
      }

      this.referee.setTankCooldown("team_a", i, TANK_STATS.attack_cooldown_ticks);
      teamADamage[i] = TANK_STATS.damage;
    }

    for (let i = 0; i < n; i++) {
      if (aiActions[i].type !== "shoot") continue;
      const shooter = teamB.tanks[i];
      if (!shooter.is_alive) continue;
      if (this.referee.getTankCooldown("team_b", i) > 0) continue;

      let bestIdx = -1;
      let bestDist = Infinity;
      for (let j = 0; j < n; j++) {
        const t = teamA.tanks[j];
        if (!t.is_alive) continue;
        const d = Math.max(Math.abs(shooter.position.x - t.position.x), Math.abs(shooter.position.y - t.position.y));
        if (d < bestDist) { bestDist = d; bestIdx = j; }
      }
      if (bestIdx < 0 || bestDist > TANK_STATS.range) continue;

      if (Math.random() >= TANK_STATS.ai_accuracy) {
        this.referee.setTankCooldown("team_b", i, TANK_STATS.attack_cooldown_ticks);
        continue;
      }

      this.referee.setTankCooldown("team_b", i, TANK_STATS.attack_cooldown_ticks);
      teamBDamage[i] = TANK_STATS.damage;
    }

    // ===== 4. 应用伤害 =====
    let totalDmgAtoB = 0;
    for (let i = 0; i < n; i++) {
      if (teamADamage[i] <= 0) continue;
      // 找 shooter tank 的最近目标
      const shooter = teamA.tanks[i];
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let j = 0; j < n; j++) {
        const t = teamB.tanks[j];
        if (!t.is_alive) continue;
        const d = Math.max(Math.abs(shooter.position.x - t.position.x), Math.abs(shooter.position.y - t.position.y));
        if (d < bestDist) { bestDist = d; bestIdx = j; }
      }
      if (bestIdx < 0) continue;
      const prevHp = teamB.tanks[bestIdx].health;
      teamB.tanks[bestIdx].health = Math.max(0, prevHp - teamADamage[i]);
      totalDmgAtoB += teamADamage[i];
      const killed = teamB.tanks[bestIdx].health <= 0;
      if (killed) teamB.tanks[bestIdx].is_alive = false;

      // First blood
      if (!this.firstBloodEmitted) {
        this.firstBloodEmitted = true;
        console.info(`[BATTLE] FIRST_BLOOD tick=${tickNum} team=A tank=A${i} → B${bestIdx} dmg=${teamADamage[i]}`);
        this.emit({ type: "key_event", eventType: "FIRST_BLOOD", tickNum, actor: "team_a", actorIndex: i, description: `Tank A${i} 先手命中` });
      }
      if (killed) {
        console.info(`[BATTLE] KILL tick=${tickNum} team=A tank=A${i} killed B${bestIdx}`);
      }
      this.emit({ type: "hit", tickNum, shooter: "team_a", target: "team_b", shooterIndex: i, targetIndex: bestIdx, damage: teamADamage[i], killed });
    }

    let totalDmgBtoA = 0;
    for (let i = 0; i < n; i++) {
      if (teamBDamage[i] <= 0) continue;
      const shooter = teamB.tanks[i];
      let bestIdx = -1;
      let bestDist = Infinity;
      for (let j = 0; j < n; j++) {
        const t = teamA.tanks[j];
        if (!t.is_alive) continue;
        const d = Math.max(Math.abs(shooter.position.x - t.position.x), Math.abs(shooter.position.y - t.position.y));
        if (d < bestDist) { bestDist = d; bestIdx = j; }
      }
      if (bestIdx < 0) continue;
      const prevHp = teamA.tanks[bestIdx].health;
      teamA.tanks[bestIdx].health = Math.max(0, prevHp - teamBDamage[i]);
      totalDmgBtoA += teamBDamage[i];
      const killed = teamA.tanks[bestIdx].health <= 0;
      if (killed) teamA.tanks[bestIdx].is_alive = false;

      if (!this.firstBloodEmitted) {
        this.firstBloodEmitted = true;
        console.info(`[BATTLE] FIRST_BLOOD tick=${tickNum} team=B tank=B${i} → A${bestIdx} dmg=${teamBDamage[i]}`);
        this.emit({ type: "key_event", eventType: "FIRST_BLOOD", tickNum, actor: "team_b", actorIndex: i, description: `Tank B${i} 先手命中` });
      }
      if (killed) {
        console.info(`[BATTLE] KILL tick=${tickNum} team=B tank=B${i} killed A${bestIdx}`);
      }
      this.emit({ type: "hit", tickNum, shooter: "team_b", target: "team_a", shooterIndex: i, targetIndex: bestIdx, damage: teamBDamage[i], killed });
    }

    // ===== 5. cooldown 自然冷却 =====
    this.referee.tickCooldowns(state);

    // ===== 6. death events =====
    for (let i = 0; i < n; i++) {
      if (teamA.tanks[i].health <= 0 && teamA.tanks[i].is_alive === false) {
        teamA.tanks[i].is_alive = false;
        this.emit({ type: "key_event", eventType: "DEATH", tickNum, actor: "team_a", actorIndex: i, description: `Tank A${i} 阵亡` });
      }
      if (teamB.tanks[i].health <= 0 && teamB.tanks[i].is_alive === false) {
        teamB.tanks[i].is_alive = false;
        this.emit({ type: "key_event", eventType: "DEATH", tickNum, actor: "team_b", actorIndex: i, description: `Tank B${i} 阵亡` });
      }
    }

    // ===== 7. 同步回 self/enemy（渲染层向后兼容）=====
    this.syncTeamToSelfEnemy();

    // ===== 8. emit tick =====
    this.emit({
      type: "tick", tickNum,
      state: JSON.parse(JSON.stringify(state)) as BattleState,
      actions: { team_a: playerActions[0], team_b: aiActions[0] },
      actionsMulti: { team_a: playerActions, team_b: aiActions },
      logs: { team_a: allPlayerLogs, team_b: allAiLogs },
      damage: { team_a_to_team_b: totalDmgAtoB, team_b_to_team_a: totalDmgBtoA },
      damageMulti: { team_a_to_team_b: teamADamage, team_b_to_team_a: teamBDamage },
      tankLogs: { team_a: playerTankLogs, team_b: aiTankLogs },
    });
  }

  // ===== V3: 团队状态同步（双向）=====
  /** 将 team_a/team_b 同步回 self/enemy（渲染层向后兼容） */
  private syncTeamToSelfEnemy(): void {
    if (!this.battleState) return;
    if (!this.battleState.team_a || !this.battleState.team_b) return;

    // team_a → self（取第一个存活 tanks，没有则取 tanks[0]）
    const aAlive = this.battleState.team_a.tanks.find((t) => t.is_alive) ?? this.battleState.team_a.tanks[0];
    this.battleState.self.position = { ...aAlive.position };
    this.battleState.self.health = aAlive.health;
    this.battleState.self.is_alive = aAlive.is_alive;
    this.battleState.self.cooldown_left = this.referee?.getTankCooldown("team_a", aAlive.tank_index) ?? 0;

    const bAlive = this.battleState.team_b.tanks.find((t) => t.is_alive) ?? this.battleState.team_b.tanks[0];
    this.battleState.enemy.position = { ...bAlive.position };
    this.battleState.enemy.health = bAlive.health;
    this.battleState.enemy.is_alive = bAlive.is_alive;
    this._enemyCooldownLeft = this.referee?.getTankCooldown("team_b", bAlive.tank_index) ?? 0;
  }

  /** 将 self/enemy 数据同步到 team_a/team_b（裁判判定用 — 1v1 向后兼容） */
  private syncTeamState(): void {
    if (!this.battleState) return;

    if (this.battleState.team_a && this.battleState.team_a.tanks.length > 0) {
      const t = this.battleState.team_a.tanks[0];
      t.position = { ...this.battleState.self.position };
      t.health = this.battleState.self.health;
      t.is_alive = this.battleState.self.is_alive;
      t.cooldown_left = this.battleState.self.cooldown_left;
    }

    if (this.battleState.team_b && this.battleState.team_b.tanks.length > 0) {
      const t = this.battleState.team_b.tanks[0];
      t.position = { ...this.battleState.enemy.position };
      t.health = this.battleState.enemy.health;
      t.is_alive = this.battleState.enemy.is_alive;
      t.cooldown_left = this._enemyCooldownLeft;
    }
  }

  private cleanup() {
    // V2: 战斗期 Worker 由 dualRuntime.dispose() 管理
    // 这里只处理 demo 兼容路径的 BotWorkerHandle
    this.referee = null; // Phase 3: 清理裁判
    if (this.dualRuntime) {
      this.dualRuntime.dispose();
      this.dualRuntime = null;
    }
    // P1-#4:战斗结束还 worker 到池(health=true,下个战斗可直接复用)
    if (this.playerHandle) releaseWorker(this.playerHandle.worker, true);
    if (this.aiHandle) releaseWorker(this.aiHandle.worker, true);
    this.playerHandle = null;
    this.aiHandle = null;
  }
}
